'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Index, Configure, useInstantSearch, useSearchBox, SearchBox } from 'react-instantsearch';
import { ALGOLIA_INDEX_NAME, SORT_BY_OPTIONS } from 'commons/constants';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import ProductListingPageName from 'components/consumer/ProductListingPage/components/ProductListingPageName';
import DesktopShowHideFiltersButton from 'components/consumer/ProductListingPage/components/desktop/DesktopShowHideFiltersButton';
import MobileGridColumnToggle from 'components/consumer/ProductListingPage/components/mobile/MobileGridColumnToggle';
import DesktopProductListingSidebarContainer from 'components/consumer/ProductListingPage/components/desktop/DesktopProductListingSidebarContainer';
import useScreen from 'hooks/useScreen';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import AdvancedSearchProductHits from 'components/consumer/consumer-search/advanced-search-next/components/AdvancedSearchProductHits';
import DesktopProductListingColorFilter from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/DesktopProductListingColorFilter';
import ProductListingSortBy from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/ProductListingSortBy';
import MobileProductFilters from 'components/consumer/consumer-search/advanced-search-next/components/AlgoliaHits/components/MobileProductFilters';
import ProductListingPageWidgets from 'components/consumer/ProductListingPageWidgets';
import ProductListingPageDescriptionShell from 'components/consumer/consumer-search/advanced-search-next/components/ProductListingPageDescriptionUI';
import useCategoryAlgoliaAnalytics from '../hooks/useCategoryAlgoliaAnalytics';
import CategoryAlgoliaSidebar from './CategoryAlgoliaSidebar';
import CategoryAlgoliaSEO from './CategoryAlgoliaSEO';
import CategoryAlgoliaJSONLD from './CategoryAlgoliaJSONLD';
import VirtualSearchBox from './VirtualSearchBox';
const CategoryAlgoliaLayout = t0 => {
  const $ = _c(81);
  const {
    slug,
    serverCategoryData
  } = t0;
  const {
    isMobile
  } = useScreen();
  const {
    currentProductIndex,
    isReadyToShipActive,
    minWidth,
    maxWidth
  } = useAdvancedSearchContext();
  const {
    results,
    status
  } = useInstantSearch();
  const {
    query: parentQuery
  } = useSearchBox();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [enrichedProducts, setEnrichedProducts] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsSidebarOpen(_temp);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const toggleSidebar = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = products => {
      setEnrichedProducts(products);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleProductsChange = t3;
  const categoryData = serverCategoryData?.getCategoryPage;
  const pageName = categoryData?.pageName || slug;
  const t4 = categoryData?.pageId;
  let t5;
  if ($[3] !== enrichedProducts || $[4] !== pageName || $[5] !== t4) {
    t5 = {
      pageId: t4,
      pageName,
      products: enrichedProducts
    };
    $[3] = enrichedProducts;
    $[4] = pageName;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useCategoryAlgoliaAnalytics(t5);
  const productsCount = results?.nbHits || 0;
  const hasProductsLoaded = status === "idle" || status === "error";
  const resultsCount = hasProductsLoaded && productsCount > 0 ? productsCount : null;
  const resultsCountText = resultsCount != null ? `${resultsCount} Products` : null;
  const t6 = `categories:${slug}`;
  let filters;
  if ($[7] !== isReadyToShipActive || $[8] !== t6) {
    filters = [t6];
    if (isReadyToShipActive) {
      filters.push("is_in_stock:true");
    }
    $[7] = isReadyToShipActive;
    $[8] = t6;
    $[9] = filters;
  } else {
    filters = $[9];
  }
  const secondaryFacetFilters = filters;
  let filters_0;
  if ($[10] !== maxWidth || $[11] !== minWidth) {
    filters_0 = ["item_availability=0", "visibility>1", "visibility<4"];
    const minNum = parseInt(minWidth, 10);
    const maxNum = parseInt(maxWidth, 10);
    if (!Number.isNaN(minNum) && minNum >= 1) {
      filters_0.push(`width>=${minNum}`);
    }
    if (!Number.isNaN(maxNum) && maxNum <= 300) {
      filters_0.push(`width<=${maxNum}`);
    }
    $[10] = maxWidth;
    $[11] = minWidth;
    $[12] = filters_0;
  } else {
    filters_0 = $[12];
  }
  const secondaryNumericFilters = filters_0;
  const isProductsIndex = currentProductIndex === ALGOLIA_INDEX_NAME.PRODUCTS;
  let t7;
  if ($[13] !== categoryData) {
    t7 = <CategoryAlgoliaSEO categoryData={categoryData} />;
    $[13] = categoryData;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = categoryData?.pageLongName || pageName;
  const t9 = categoryData?.pageLongDescription || "";
  const t10 = categoryData?.pageId;
  const t11 = results?.nbHits;
  let t12;
  if ($[15] !== currentProductIndex || $[16] !== enrichedProducts || $[17] !== hasProductsLoaded || $[18] !== t10 || $[19] !== t11 || $[20] !== t8 || $[21] !== t9) {
    t12 = <CategoryAlgoliaJSONLD products={enrichedProducts} pageName={t8} pageDescription={t9} pageId={t10} totalHits={t11} currentProductIndex={currentProductIndex} hasProductsLoaded={hasProductsLoaded} />;
    $[15] = currentProductIndex;
    $[16] = enrichedProducts;
    $[17] = hasProductsLoaded;
    $[18] = t10;
    $[19] = t11;
    $[20] = t8;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = classNames("px-3 py-4 space-y-1", "md:px-0 md:py-0 md:space-y-0", "md:items-center md:bg-white md:flex md:h-[53px] md:sticky md:z-[11] md:gap-5 md:justify-between", "md:top-[117px]");
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== pageName) {
    t14 = <ProductListingPageName pageName={pageName} showPageNameWhileLoading={true} />;
    $[24] = pageName;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== resultsCountText) {
    t15 = resultsCountText && <span className="text-sm text-gray-light2 font-normal">{resultsCountText}</span>;
    $[26] = resultsCountText;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <DesktopShowHideFiltersButton onClick={toggleSidebar} />;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t14 || $[30] !== t15) {
    t17 = <div className="md:flex md:gap-5 md:items-center">{t14}{t15}{t16}</div>;
    $[29] = t14;
    $[30] = t15;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = classNames("flex flex-row justify-between gap-3 py-3", "md:block md:gap-0 md:py-0");
    t19 = classNames("flex-1 flex gap-1", "md:flex-initial md:block md:gap-0");
    $[32] = t18;
    $[33] = t19;
  } else {
    t18 = $[32];
    t19 = $[33];
  }
  let t20;
  if ($[34] !== isMobile || $[35] !== productsCount) {
    t20 = isMobile && <><MobileProductFilters productsCount={productsCount} /><div className="flex-1 min-w-[140px] flex items-center"><ProductListingSortBy sortByOptions={SORT_BY_OPTIONS.filter(_temp2)} /></div></>;
    $[34] = isMobile;
    $[35] = productsCount;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== isMobile) {
    t21 = !isMobile && <div className="flex-1 md:flex-initial md:flex md:flex-row md:gap-4 md:items-center"><DesktopProductListingColorFilter /><ProductListingSortBy sortByOptions={SORT_BY_OPTIONS.filter(_temp3)} /></div>;
    $[37] = isMobile;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== t20 || $[40] !== t21) {
    t22 = <div className={t19}>{t20}{t21}</div>;
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <MobileGridColumnToggle />;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== t22) {
    t24 = <div className={t18}>{t22}{t23}</div>;
    $[43] = t22;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t17 || $[46] !== t24) {
    t25 = <div className={t13}>{t17}{t24}</div>;
    $[45] = t17;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== pageName) {
    t26 = <CategoryAlgoliaSidebar pageName={pageName} />;
    $[48] = pageName;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== isSidebarOpen || $[51] !== t26) {
    t27 = <div className="hidden md:block"><DesktopProductListingSidebarContainer isOpen={isSidebarOpen}>{t26}</DesktopProductListingSidebarContainer></div>;
    $[50] = isSidebarOpen;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== isProductsIndex) {
    t28 = isProductsIndex && <AdvancedSearchProductHits onProductsChange={handleProductsChange} />;
    $[53] = isProductsIndex;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== currentProductIndex || $[56] !== isProductsIndex || $[57] !== parentQuery || $[58] !== secondaryFacetFilters || $[59] !== secondaryNumericFilters) {
    t29 = !isProductsIndex && <Index indexName={currentProductIndex}><SearchBox classNames={{
        root: "hidden"
      }} /><VirtualSearchBox parentQuery={parentQuery} /><Configure facets={["*"]} facetFilters={secondaryFacetFilters} numericFilters={secondaryNumericFilters} attributesToRetrieve={["_highlightResult", "product_id", "name", "is_in_stock"]} clickAnalytics={true} /><AdvancedSearchProductHits onProductsChange={handleProductsChange} /></Index>;
    $[55] = currentProductIndex;
    $[56] = isProductsIndex;
    $[57] = parentQuery;
    $[58] = secondaryFacetFilters;
    $[59] = secondaryNumericFilters;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  const t30 = categoryData?.pageLongDescription || "";
  let t31;
  if ($[61] !== pageName || $[62] !== t30) {
    t31 = <ProductListingPageDescriptionShell pageLongDescription={t30} pageName={pageName} />;
    $[61] = pageName;
    $[62] = t30;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== t28 || $[65] !== t29 || $[66] !== t31) {
    t32 = <div className="md:space-y-8 md:mb-8 md:flex-1">{t28}{t29}{t31}</div>;
    $[64] = t28;
    $[65] = t29;
    $[66] = t31;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  let t33;
  if ($[68] !== t27 || $[69] !== t32) {
    t33 = <div className="md:flex md:flex-row md:gap md:mt-2">{t27}{t32}</div>;
    $[68] = t27;
    $[69] = t32;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  const t34 = categoryData?.pageId;
  let t35;
  if ($[71] !== t34) {
    t35 = <ProductListingPageWidgets categoryId={t34} />;
    $[71] = t34;
    $[72] = t35;
  } else {
    t35 = $[72];
  }
  let t36;
  if ($[73] !== t25 || $[74] !== t33 || $[75] !== t35) {
    t36 = <FixedWidthContainer wide={true} noMobilePadding={true}>{t25}{t33}{t35}</FixedWidthContainer>;
    $[73] = t25;
    $[74] = t33;
    $[75] = t35;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[77] !== t12 || $[78] !== t36 || $[79] !== t7) {
    t37 = <>{t7}{t12}{t36}</>;
    $[77] = t12;
    $[78] = t36;
    $[79] = t7;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  return t37;
};
CategoryAlgoliaLayout.propTypes = {
  slug: PropTypes.string.isRequired,
  serverCategoryData: PropTypes.object
};
export default CategoryAlgoliaLayout;
function _temp(prev) {
  return !prev;
}
function _temp2(option) {
  return option.value !== "newest";
}
function _temp3(option_0) {
  return option_0.value !== "newest";
}